import { createReducer } from '@reduxjs/toolkit';
import {
  AnnualReviewDate,
  ENROLLMENT_STEPS_MULTIPLE,
  ENROLLMENT_STEPS_SINGLE,
  EnrollmentState,
  EnrollmentStep,
  EnrollmentType,
  TemplateUploadStatus,
} from './enrollmentModel';
import {
  createEnrollment,
  createEnrollmentSuccess,
  createEnrollmentFail,
  setAccountAdminOne,
  setAccountAdminOneSuccess,
  setAccountAdminOneFail,
  setAccountAdminTwo,
  setAccountAdminTwoSuccess,
  setAccountAdminTwoFail,
  setActiveStep,
  setEnrollmentType,
  setEntityInfo,
  setIsDFINAdmin,
  setIsDFINAdminSuccess,
  setIsDFINAdminFail,
  setEntityInfoFail,
  setEntityInfoSuccess,
  uploadTemplateFile,
  uploadTemplateFileSuccess,
  uploadTemplateFileFail,
  enroll,
  enrollFail,
  enrollSuccess,
  getEdgarStatus,
  getEdgarStatusSuccess,
  getEdgarStatusFail,
  getLatestEnrollment,
  getLatestEnrollmentFail,
  getLatestEnrollmentSuccess,
  continueEnrollment,
  continueEnrollmentFail,
  continueEnrollmentSuccess,
  resetEnrollment,
  resetStepIdsAfterBulk,
} from './enrollmentActions';

export const initialState: EnrollmentState = {
  activeStep: ENROLLMENT_STEPS_SINGLE[0],
  enrollmentSteps: ENROLLMENT_STEPS_SINGLE,
  enrollmentType: EnrollmentType.SINGLE_ENTITY,
  enrollmentStepIds: {
    pathSelection: '',
    entityInformation: '',
    accountAdministrator1: '',
    accountAdministrator2: '',
    bulk: '',
  },
  isDFINAdmin: true,
  isLoading: false,
  enrollmentId: '',
  entityInfo: {
    reviewDate: AnnualReviewDate.DECEMBER_31,
    cik: '',
    ccc: '',
    passphrase: '',
  },
  templateUploadStatus: TemplateUploadStatus.NOT_STARTED,
  accountAdminOne: {
    email: '',
    firstName: '',
    lastName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    country: 'X1',
    zipCode: '',
    phoneNumber: '',
    nonUSAddress: false,
  },
  accountAdminTwo: {
    email: '',
    firstName: '',
    lastName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    country: 'X1',
    zipCode: '',
    phoneNumber: '',
    nonUSAddress: false,
  },
  isEdgarAvailable: false,
};

const enrollmentReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(setActiveStep, (state: EnrollmentState, action) => {
      state.activeStep = action.payload.step;
      if (action.payload.step === EnrollmentStep.ACCOUNT_ADMINISTRATOR_1) {
        state.enrollmentSteps = ENROLLMENT_STEPS_SINGLE;
      }
    })
    .addCase(createEnrollment, (state: EnrollmentState) => {
      state.isLoading = true;
    })
    .addCase(createEnrollmentSuccess, (state: EnrollmentState, action) => {
      Object.assign(state, {
        ...initialState,
        enrollmentId: action.payload.enrollmentId,
        isLoading: false,
      });
    })
    .addCase(createEnrollmentFail, (state: EnrollmentState) => {
      state.isLoading = false;
    })
    .addCase(setIsDFINAdmin, (state: EnrollmentState) => {
      state.isLoading = true;
    })
    .addCase(setIsDFINAdminSuccess, (state: EnrollmentState, action) => {
      state.isDFINAdmin = action.payload.isDFINAdmin;
      state.enrollmentStepIds.pathSelection = action.payload.stepId;
      state.isLoading = false;
    })
    .addCase(setIsDFINAdminFail, (state: EnrollmentState) => {
      state.isLoading = false;
    })
    .addCase(setEntityInfo, (state: EnrollmentState) => {
      state.isLoading = true;
    })
    .addCase(setEntityInfoSuccess, (state: EnrollmentState, action) => {
      state.entityInfo = action.payload.entityInfo;
      state.enrollmentStepIds.entityInformation = action.payload.stepId;
      state.isLoading = false;
    })
    .addCase(setEntityInfoFail, (state: EnrollmentState) => {
      state.isLoading = false;
    })
    .addCase(uploadTemplateFile, (state: EnrollmentState) => {
      state.templateUploadStatus = TemplateUploadStatus.UPLOADING;
      state.templateUploadError = initialState.templateUploadError;
    })
    .addCase(uploadTemplateFileSuccess, (state: EnrollmentState, action) => {
      state.templateUploadStatus = TemplateUploadStatus.SUCCESS;
      state.entityInfo.names = action.payload.entityNames;
    })
    .addCase(uploadTemplateFileFail, (state: EnrollmentState, action) => {
      state.templateUploadStatus = TemplateUploadStatus.FAIL;
      state.templateUploadError = action.payload.errorMsg;
    })
    .addCase(setEnrollmentType, (state: EnrollmentState, action) => {
      state.enrollmentType = action.payload.enrollmentType;
      state.enrollmentSteps =
        action.payload.enrollmentType === EnrollmentType.SINGLE_ENTITY
          ? ENROLLMENT_STEPS_SINGLE
          : ENROLLMENT_STEPS_MULTIPLE;
    })
    .addCase(setAccountAdminOne, (state: EnrollmentState) => {
      state.isLoading = true;
    })
    .addCase(setAccountAdminOneSuccess, (state: EnrollmentState, action) => {
      state.accountAdminOne = action.payload.accountAdminOne;
      state.enrollmentStepIds.accountAdministrator1 = action.payload.stepId;
      state.isLoading = false;
    })
    .addCase(setAccountAdminOneFail, (state: EnrollmentState) => {
      state.isLoading = false;
    })
    .addCase(setAccountAdminTwo, (state: EnrollmentState) => {
      state.isLoading = true;
    })
    .addCase(setAccountAdminTwoSuccess, (state: EnrollmentState, action) => {
      state.accountAdminTwo = action.payload.accountAdminTwo;
      state.enrollmentStepIds.accountAdministrator2 = action.payload.stepId;
      state.isLoading = false;
    })
    .addCase(setAccountAdminTwoFail, (state: EnrollmentState) => {
      state.isLoading = false;
    })
    .addCase(enroll, (state: EnrollmentState) => {
      state.isLoading = true;
    })
    .addCase(enrollFail, (state: EnrollmentState) => {
      state.isLoading = false;
    })
    .addCase(enrollSuccess, (state: EnrollmentState) => {
      state.isLoading = false;
    })
    .addCase(getEdgarStatus, (state: EnrollmentState) => {
      state.isLoading = true;
    })
    .addCase(getEdgarStatusFail, (state: EnrollmentState) => {
      state.isLoading = false;
      state.isEdgarAvailable = false;
    })
    .addCase(getEdgarStatusSuccess, (state: EnrollmentState, action) => {
      state.isLoading = false;
      state.isEdgarAvailable = action.payload.isEdgarAvailable;
    })
    .addCase(getLatestEnrollment, (state: EnrollmentState) => {
      state.isLoading = true;
      state.latestEnrollmentId = initialState.latestEnrollmentId;
    })
    .addCase(getLatestEnrollmentFail, (state: EnrollmentState) => {
      state.isLoading = false;
    })
    .addCase(getLatestEnrollmentSuccess, (state: EnrollmentState, action) => {
      state.isLoading = false;
      state.latestEnrollmentId = action.payload.enrollmentId;
    })
    .addCase(continueEnrollment, (state: EnrollmentState) => {
      state.isLoading = true;
    })
    .addCase(continueEnrollmentFail, (state: EnrollmentState) => {
      state.isLoading = false;
    })
    .addCase(continueEnrollmentSuccess, (state: EnrollmentState, action) => {
      state.enrollmentId = action.payload.enrollmentId;
      state.isLoading = false;
    })
    .addCase(resetEnrollment, () => initialState)
    .addCase(resetStepIdsAfterBulk, (state: EnrollmentState) => {
      state.enrollmentStepIds.entityInformation = '';
      state.enrollmentStepIds.accountAdministrator1 = '';
      state.enrollmentStepIds.accountAdministrator2 = '';
    });
});

export default enrollmentReducer;
